/**
 * Mark-up geometry: normalised page space in, screen shapes out.
 *
 * Every stored coordinate is 0…1 of the page box in the page's OWN orientation, so a
 * mark-up drawn at 1920px lands in the same place on an A3 tablet and in a 200-dpi
 * server-rendered commented copy. Rotating the view rotates the pointer back into page
 * space before anything is stored — the record is of the sheet, not of the way one
 * reviewer happened to be looking at it.
 */

// ── rotation, both ways ────────────────────────────────────────────────────
export function toPageSpace(x, y, rotation) {
  const r = ((rotation % 360) + 360) % 360;
  if (r === 90) return [y, 1 - x];
  if (r === 180) return [1 - x, 1 - y];
  if (r === 270) return [1 - y, x];
  return [x, y];
}

export function fromPageSpace(x, y, rotation) {
  const r = ((rotation % 360) + 360) % 360;
  if (r === 90) return [1 - y, x];
  if (r === 180) return [1 - x, 1 - y];
  if (r === 270) return [y, 1 - x];
  return [x, y];
}

export const px = (pts, w, h, rotation) =>
  (pts || []).map(([x, y]) => {
    const [dx, dy] = fromPageSpace(x, y, rotation);
    return [dx * w, dy * h];
  });

export const flat = (pairs) => pairs.reduce((a, p) => a.concat(p), []);

// ── revision cloud ─────────────────────────────────────────────────────────
// The EPC convention, drawn as scallops around the box rather than as an image, so it
// stays a cloud at any zoom and can be deleted again.
export function cloudPoints([p1, p2], radius = 9) {
  const [x1, y1] = [Math.min(p1[0], p2[0]), Math.min(p1[1], p2[1])];
  const [x2, y2] = [Math.max(p1[0], p2[0]), Math.max(p1[1], p2[1])];
  const out = [];
  const arc = (ax, ay, bx, by) => {
    const dx = bx - ax, dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;
    const n = Math.max(1, Math.round(len / (radius * 2)));
    for (let i = 0; i < n; i += 1) {
      const t0 = i / n, t1 = (i + 1) / n, tm = (t0 + t1) / 2;
      const mx = ax + dx * tm, my = ay + dy * tm;
      const nx = -dy / len, ny = dx / len;
      out.push([ax + dx * t0, ay + dy * t0]);
      out.push([mx + nx * radius, my + ny * radius]);
    }
  };
  arc(x1, y1, x2, y1); arc(x2, y1, x2, y2); arc(x2, y2, x1, y2); arc(x1, y2, x1, y1);
  out.push([x1, y1]);
  return out;
}

// ── measurement, with its units stated ─────────────────────────────────────
/**
 * A distance between two normalised points, in the sheet's own units.
 * PDF user space is 1/72 inch, so page points convert exactly to mm. What does NOT
 * convert is the drawing scale: 1:50 on the title block is not recorded in the PDF, so
 * the measurement is reported as a distance ON THE SHEET and labelled as such. Printing
 * "4,250 mm" for a 85 mm line on a 1:50 drawing would be a fabrication.
 */
export function measure(p1, p2, pageSizePt) {
  const wPt = pageSizePt?.width || 595.28;
  const hPt = pageSizePt?.height || 841.89;
  const dxPt = (p2[0] - p1[0]) * wPt;
  const dyPt = (p2[1] - p1[1]) * hPt;
  const pts = Math.hypot(dxPt, dyPt);
  const mm = (pts / 72) * 25.4;
  return {
    points: Number(pts.toFixed(2)),
    mm: Number(mm.toFixed(2)),
    inches: Number((pts / 72).toFixed(3)),
    label: `${mm.toFixed(1)} mm on the sheet`,
    unitsProvenance:
      'Measured on the sheet in PDF user space (1/72 inch) and converted exactly to '
      + 'millimetres. The drawing scale is not recorded in the file, so this is a distance '
      + 'on the sheet and not a real-world dimension.',
  };
}

export const TOOL_ICON_ORDER = [
  'rectangle', 'ellipse', 'cloud', 'arrow', 'polyline', 'freehand',
  'highlight', 'text', 'dimension', 'stamp',
];

export const STAMPS = ['APPROVED', 'APPROVED WITH COMMENTS', 'REVISE AND RESUBMIT',
  'FOR INFORMATION', 'VOID', 'SUPERSEDED', 'AS-BUILT'];
